/**
 * The video downlink post stack (SPEC §3.3.2), as three full-screen passes plus a two-step
 * metering chain.
 *
 * The ten conceptual effects compile into three passes, in the order the light actually meets
 * them:
 *
 *   1. OPTICS    — what happens in front of and inside the lens: frame jitter and tear, rolling
 *                  shutter, barrel distortion, chromatic aberration, atmospheric veil, vignette.
 *                  FXAA rides here too, because it wants the geometry edges before resampling.
 *   2. SIGNAL    — what the sensor and the camera's processing do: exposure, highlight shoulder,
 *                  the washed teal-amber grade, sensor grain.
 *   3. TRANSMIT  — what the radio link does: 8×8 block quantisation, driven by link quality.
 *
 * Haze sits in OPTICS rather than with the grade because it is in the world, not in the camera:
 * metering has to see it, or flying into a hazy sun would meter as if the air were clear.
 *
 * No colour-space conversion happens anywhere in the stack. The world shaders (M3) write
 * display-referred values straight to the frame, so the stack inherits that convention; the
 * half-float targets keep the sun's several-times-white highlights intact for the shoulder to
 * roll off.
 *
 * GLSL lives in .ts files as template literals for the same reason as the M3 shaders: one
 * typecheck, lint and bundling path, no loader configuration.
 */

/** Rec. 709 luma weights, used by FXAA, the meter and the grade. */
const LUMA = /* glsl */ `
const vec3 LUMA_WEIGHTS = vec3(0.2126, 0.7152, 0.0722);
float luma(vec3 colour) { return dot(colour, LUMA_WEIGHTS); }
`;

/** Full-screen triangle-pair vertex shader: clip space straight through, no camera involved. */
export const FULLSCREEN_VERTEX = /* glsl */ `
varying vec2 vUv;

void main() {
  vUv = uv;
  gl_Position = vec4(position.xy, 0.0, 1.0);
}
`;

/**
 * Pass 1 — optics.
 *
 * A frame *hold* is a texture swap on the CPU (the previous frame's target is bound as the
 * source), not a branch in here: doubling every tap to blend two frames would cost more than the
 * whole pass. Only the tear, which is a band rather than a whole frame, is resolved per fragment.
 */
export const OPTICS_FRAGMENT = /* glsl */ `
precision highp float;

uniform sampler2D tDiffuse;
uniform vec2 uTexel;          // 1 / source resolution
uniform float uAspect;

uniform float uUseDistortion;
uniform float uUseChromatic;
uniform float uUseHaze;
uniform float uUseVignette;
uniform float uUseFxaa;

uniform float uBarrelK1;
uniform float uBarrelScale;   // overscan that keeps the distorted corners inside the source
uniform float uInvCornerR2;   // normalises radius² to 1 at a corner, which is where k1 is defined
uniform vec2 uChromatic;      // channel separation at the frame edge, uv units
uniform float uSkew;          // rolling shutter, frame widths between top and bottom rows
uniform vec2 uJitter;         // whole-frame offset, uv units
uniform float uTearY;         // 1 = no tear
uniform float uTearShift;

uniform vec3 uHazeColour;
uniform float uHazeVeil;
uniform float uHazeDensity;      // per metre
uniform float uHazeMaxRange;     // metres
uniform float uCameraAltitudeM;  // above sea level
uniform float uHazeGlare;
uniform float uHazeAnisotropy;
uniform vec3 uSunDirection;
uniform vec3 uCameraRight;
uniform vec3 uCameraUp;
uniform vec3 uCameraForward;
uniform vec2 uTanHalfFov;

uniform float uVignetteStrength;
uniform float uVignetteStart;

varying vec2 vUv;

${LUMA}

/**
 * FXAA 3.11, console variant. Cheap enough to belong in a pass that is already sampling, and
 * necessary because the renderer runs with antialias off (SPEC §4.5) — MSAA cannot coexist with
 * a post stack that resamples the frame.
 */
vec3 fxaa(vec2 uv) {
  vec3 rgbNW = texture2D(tDiffuse, uv + vec2(-1.0, -1.0) * uTexel).rgb;
  vec3 rgbNE = texture2D(tDiffuse, uv + vec2(1.0, -1.0) * uTexel).rgb;
  vec3 rgbSW = texture2D(tDiffuse, uv + vec2(-1.0, 1.0) * uTexel).rgb;
  vec3 rgbSE = texture2D(tDiffuse, uv + vec2(1.0, 1.0) * uTexel).rgb;
  vec3 rgbM = texture2D(tDiffuse, uv).rgb;

  float lumaNW = luma(rgbNW);
  float lumaNE = luma(rgbNE);
  float lumaSW = luma(rgbSW);
  float lumaSE = luma(rgbSE);
  float lumaM = luma(rgbM);

  float lumaMin = min(lumaM, min(min(lumaNW, lumaNE), min(lumaSW, lumaSE)));
  float lumaMax = max(lumaM, max(max(lumaNW, lumaNE), max(lumaSW, lumaSE)));
  if (lumaMax - lumaMin < lumaMax * 0.0625) return rgbM;

  vec2 direction;
  direction.x = -((lumaNW + lumaNE) - (lumaSW + lumaSE));
  direction.y = (lumaNW + lumaSW) - (lumaNE + lumaSE);

  float reduce = max((lumaNW + lumaNE + lumaSW + lumaSE) * 0.03125, 0.0078125);
  float rcpMin = 1.0 / (min(abs(direction.x), abs(direction.y)) + reduce);
  direction = clamp(direction * rcpMin, -8.0, 8.0) * uTexel;

  vec3 rgbA = 0.5 * (
    texture2D(tDiffuse, uv + direction * (1.0 / 3.0 - 0.5)).rgb +
    texture2D(tDiffuse, uv + direction * (2.0 / 3.0 - 0.5)).rgb);
  vec3 rgbB = rgbA * 0.5 + 0.25 * (
    texture2D(tDiffuse, uv + direction * -0.5).rgb +
    texture2D(tDiffuse, uv + direction * 0.5).rgb);

  float lumaB = luma(rgbB);
  return (lumaB < lumaMin || lumaB > lumaMax) ? rgbA : rgbB;
}

/** Henyey–Greenstein: the forward-scattering lobe that makes a low sun veil the whole frame. */
float miePhase(float cosTheta, float g) {
  float gg = g * g;
  float denominator = 1.0 + gg - 2.0 * g * cosTheta;
  return (1.0 - gg) / (4.0 * 3.14159265 * pow(max(denominator, 1e-4), 1.5));
}

void main() {
  vec2 uv = vUv;

  // Frame jitter, then the tear band: both displace where this row is read from.
  uv += uJitter;
  uv.x += step(uTearY, vUv.y) * uTearShift;

  // Rolling shutter: the bottom row is read out later than the top, so during a right yaw it
  // catches the world further round and the picture leans. Positive uSkew is a right yaw.
  uv.x -= (uv.y - 0.5) * uSkew;

  // Barrel distortion, r' = r·(1 + k1·r²). Sampling further out for larger radii squeezes the
  // edges inward, which is what a wide-angle lens does. Radius is normalised to 1 at a corner —
  // the convention k1 is quoted in — and the overscan crops the 1/(1 + k1) the mapping would
  // otherwise have to fetch from outside the frame.
  if (uUseDistortion > 0.5) {
    vec2 centred = (uv - 0.5) * 2.0;
    centred.x *= uAspect;
    float r2 = dot(centred, centred) * uInvCornerR2;
    centred *= (1.0 + uBarrelK1 * r2) * uBarrelScale;
    centred.x /= uAspect;
    uv = centred * 0.5 + 0.5;
  }

  vec3 colour = uUseFxaa > 0.5 ? fxaa(uv) : texture2D(tDiffuse, uv).rgb;

  // Chromatic aberration, applied as the *difference* two radially offset taps make, so the red
  // and blue channels keep whatever FXAA just did for them.
  if (uUseChromatic > 0.5) {
    vec2 offset = (uv - 0.5) * 2.0 * uChromatic;
    vec3 plain = texture2D(tDiffuse, uv).rgb;
    colour.r += texture2D(tDiffuse, uv + offset).r - plain.r;
    colour.b += texture2D(tDiffuse, uv - offset).b - plain.b;
  }

  // Atmospheric veil, exponential-squared in optical depth (SPEC §3.3.2 item 5).
  //
  // Path length comes from where the view ray meets the sea rather than from a depth buffer: the
  // world's own materials do not all write depth the same way, and the ground plane is an honest
  // approximation for a camera that spends its life looking at a coastline. The world shaders
  // already apply exact per-pixel extinction to the geometry; this layer adds what depends on
  // where the camera is *pointing* — the thickening toward the horizon and the sun's veiling
  // glare, which is what actually blinds an into-sun ingress.
  if (uUseHaze > 0.5) {
    vec2 ndc = (uv - 0.5) * 2.0;
    vec3 ray = normalize(
      uCameraForward + uCameraRight * ndc.x * uTanHalfFov.x + uCameraUp * ndc.y * uTanHalfFov.y);

    float range = uHazeMaxRange;
    if (ray.y < -0.002) range = min(uCameraAltitudeM / -ray.y, uHazeMaxRange);
    float optical = range * uHazeDensity;
    float veil = uHazeVeil * (1.0 - exp(-optical * optical));
    float glare = uHazeGlare * miePhase(dot(ray, uSunDirection), uHazeAnisotropy);

    colour = mix(colour, uHazeColour * (1.0 + glare * 2.0), clamp(veil + glare, 0.0, 0.92));
  }

  // Vignette, on the undistorted frame position so the darkening stays centred on the picture.
  if (uUseVignette > 0.5) {
    float radius = length((vUv - 0.5) * 2.0) * 0.70710678;
    colour *= 1.0 - uVignetteStrength * smoothstep(uVignetteStart, 1.0, radius);
  }

  gl_FragColor = vec4(colour, 1.0);
}
`;

/**
 * Pass 2 — sensor and camera processing: exposure, highlight shoulder, grade, grain.
 *
 * The shoulder is unconditional. It is the sensor's transfer curve rather than an effect, and
 * without it a sun in frame clips to a flat white disc instead of blooming out across the picture
 * — which is the whole point of making exposure a gameplay system.
 */
export const SIGNAL_FRAGMENT = /* glsl */ `
precision highp float;

uniform sampler2D tDiffuse;
uniform vec2 uResolution;

uniform float uUseGrade;
uniform float uUseGrain;

uniform float uExposure;      // linear gain
uniform float uTonemapKnee;

uniform float uSaturation;
uniform float uContrast;
uniform float uLift;
uniform float uPivot;
uniform vec3 uShadowTint;
uniform vec3 uHighlightTint;
uniform float uTintStrength;

uniform float uGrainAmplitude;
uniform float uGrainShadowGain;
uniform float uGrainGain;
uniform float uGrainTime;     // stepped, not continuous, so the grain does not crawl

varying vec2 vUv;

${LUMA}

float hash21(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
}

/**
 * Highlight shoulder: linear up to the knee, then an exponential approach to white. Continuous in
 * value *and* slope at the knee, so there is no visible band where the roll-off begins.
 */
vec3 shoulder(vec3 colour) {
  vec3 headroom = vec3(1.0 - uTonemapKnee);
  vec3 excess = max(colour - uTonemapKnee, 0.0);
  vec3 rolled = uTonemapKnee + headroom * (1.0 - exp(-excess / headroom));
  return mix(colour, rolled, step(uTonemapKnee, colour));
}

void main() {
  vec3 colour = shoulder(texture2D(tDiffuse, vUv).rgb * uExposure);

  if (uUseGrade > 0.5) {
    float l = luma(colour);
    // Cyan in the shadows, amber in the highlights — the reference's washed teal-amber.
    vec3 tint = mix(uShadowTint, uHighlightTint, smoothstep(0.12, 0.7, l));
    colour = mix(colour, colour * tint, uTintStrength);
    colour = mix(vec3(l), colour, uSaturation);
    colour = (colour - uPivot) * uContrast + uPivot + uLift;
  }

  if (uUseGrain > 0.5) {
    float l = luma(colour);
    float noise = hash21(vUv * uResolution + uGrainTime) - 0.5;
    float amplitude = uGrainAmplitude * uGrainGain * (1.0 + uGrainShadowGain * (1.0 - clamp(l, 0.0, 1.0)));
    colour += noise * amplitude;
  }

  gl_FragColor = vec4(max(colour, 0.0), 1.0);
}
`;

/**
 * Pass 3 — the link itself: 8×8 block quantisation whose severity is one uniform, and the pass
 * that presents to the screen.
 *
 * Blocks are chosen by hashing the block index against the downlink frame index, so at partial
 * link a scatter of macroblocks freezes to their average colour and the rest of the picture holds
 * together — the way a real encoder starves, rather than a uniform mosaic.
 */
export const TRANSMIT_FRAGMENT = /* glsl */ `
precision highp float;

uniform sampler2D tDiffuse;
uniform float uUseBlocking;
uniform vec2 uBlocks;          // frame size in macroblocks
uniform float uBlockCoverage;
uniform float uQuantLevels;
uniform float uBlockSalt;      // downlink frame index

varying vec2 vUv;

float hash21(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
}

void main() {
  vec3 colour = texture2D(tDiffuse, vUv).rgb;

  if (uUseBlocking > 0.5) {
    vec2 block = floor(vUv * uBlocks);
    if (hash21(block + uBlockSalt) < uBlockCoverage) {
      // Collapse the macroblock to its DC term: four taps at the quarter points.
      vec2 centre = (block + 0.5) / uBlocks;
      vec2 quarter = 0.25 / uBlocks;
      colour = 0.25 * (
        texture2D(tDiffuse, centre + vec2(-quarter.x, -quarter.y)).rgb +
        texture2D(tDiffuse, centre + vec2(quarter.x, -quarter.y)).rgb +
        texture2D(tDiffuse, centre + vec2(-quarter.x, quarter.y)).rgb +
        texture2D(tDiffuse, centre + vec2(quarter.x, quarter.y)).rgb);
    }
    colour = floor(clamp(colour, 0.0, 1.0) * uQuantLevels + 0.5) / uQuantLevels;
  }

  gl_FragColor = vec4(colour, 1.0);
}
`;

/**
 * Metering, step 1 — the full frame down to a small grid of log-luminance.
 *
 * Log rather than linear, because the average of log luminance is the geometric mean, and that is
 * what an auto-exposure wants: one specular glint on the sea should not decide the gain for the
 * whole picture. The result is written as a byte so the CPU can read it back without half-float
 * decoding; `PostDriver.decodeMeterByte` is the other half of this encoding.
 */
export const METER_DOWNSAMPLE_FRAGMENT = /* glsl */ `
precision highp float;

uniform sampler2D tDiffuse;
uniform vec2 uFootprint;      // source uv covered by one output texel
uniform float uEncodeMin;     // stops
uniform float uEncodeSpan;    // stops

varying vec2 vUv;

${LUMA}

void main() {
  // 4×4 taps across this texel's footprint. Fewer taps and the meter aliases as the camera moves,
  // which reads as the gain hunting for no reason.
  vec3 total = vec3(0.0);
  for (int y = 0; y < 4; y++) {
    for (int x = 0; x < 4; x++) {
      vec2 offset = (vec2(float(x), float(y)) + 0.5) * 0.25 - 0.5;
      total += texture2D(tDiffuse, vUv + offset * uFootprint).rgb;
    }
  }

  float value = luma(total / 16.0);
  float encoded = clamp((log2(max(value, 1e-6)) - uEncodeMin) / uEncodeSpan, 0.0, 1.0);
  gl_FragColor = vec4(encoded, encoded, encoded, 1.0);
}
`;

/**
 * Metering, step 2 — the grid down to a single centre-weighted texel, which is the one pixel the
 * CPU reads back each metering interval.
 *
 * Centre-weighted because the camera is a weapon sight: what is in the middle of the frame is what
 * the player is looking at, and a bright sky filling the top third must not stop the target from
 * being visible.
 */
export const METER_REDUCE_FRAGMENT = /* glsl */ `
precision highp float;

uniform sampler2D tDiffuse;
uniform float uCentreWeight;

varying vec2 vUv;

void main() {
  float total = 0.0;
  float weightTotal = 0.0;
  for (int y = 0; y < METER_RES; y++) {
    for (int x = 0; x < METER_RES; x++) {
      vec2 uv = (vec2(float(x), float(y)) + 0.5) / float(METER_RES);
      float radius = length(uv - 0.5) * 1.41421356;
      float weight = mix(uCentreWeight, 1.0, smoothstep(0.0, 0.8, radius));
      total += texture2D(tDiffuse, uv).r * weight;
      weightTotal += weight;
    }
  }
  float value = total / weightTotal;
  gl_FragColor = vec4(value, value, value, 1.0);
}
`;
